import { AppDispatch, RootState } from 'store/types';
import {
    createCompanyInfo,
    getCompany,
    postUserFile,
    updateCompanyInfoBySiren,
    updateUserInfoById
} from 'services/network/api/payment';
import { addToast } from "../toasts/actions";
import { ListFiles, Society } from './types';
import { logError } from 'utils/logs';
import { formatBirthdayForApi } from 'store/features/investors/formatBirthday';

export function getSocietyList() {
    return async (dispatch: AppDispatch) => {
        dispatch({ type: 'FETCH_SOCIETY_REQUEST' });
        try {
            const { data } = await getCompany();
            dispatch({ type: 'FETCH_SOCIETY_SUCCESS', payload: data });
            if (data.length > 0) {
                dispatch(setSocietyChoice(data[0].id));
            }
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'FETCH_SOCIETY_FAIL', payload: err });
        }
    }
}

export function addLegalPerson() {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const { investor: society, account } = getState();
        const { iss } = getState().account;
        const formattedLegalPerson = {
            birthday: formatBirthdayForApi(society.birthday),
            address_birth: {
                address: society.birthPlace.address,
                zip_code: society.birthPlace.zipCode,
                city: society.birthPlace.city,
                country: society.birthPlace.country,
            },
            iss,
            first_name: society.firstname || account.firstname,
            last_name: society.lastname || account.lastname,
            gender: (society.gender !== 'Autre') ? society.gender : '',
            address: {
                address: society.address,
                zip_code: society.zipCode,
                city: society.city,
                country: society.country
            },
            phone: society.phone,
        };
        try {
            await updateUserInfoById(iss, formattedLegalPerson);
        } catch (err) {
            logError(err as Error);
            dispatch(addToast({
                type: 'error',
                title: 'Vos informations n\'ont pas pu être mise à jour',
                description: `Une erreur est survenue lors de l'envoi de vos données : ${err}`
            }))
        }
    };
}

export function createNewSociety(callback: () => void) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch({ type: 'CREATE_SOCIETY' });
        const params = new URLSearchParams();
        try {
            const { society } = getState();
            const formattedSociety: Society = {
                address: {
                    address: society.societyAddress,
                    zip_code: society.societyZipCode,
                    city: society.societyCity,
                    country: society.countrySociety,
                },
                siren: society.siren,
                society_name: society.nameSociety,
                rcs_city: society.rcs,
                role_person: society.role,
                form_juridique: society.societyLegal,
                iss: getState().account.iss,
                id_transaction: params.get('id_transaction') || window.localStorage.getItem('id_transaction'),
            };
            dispatch(addLegalPerson());
            if (society.id && society.id !== 'empty') {
                await updateCompanyInfoBySiren(society.siren, formattedSociety);
            } else {
                await createCompanyInfo(formattedSociety);
            }
            dispatch({ type: 'CREATE_SOCIETY_SUCCESS' });
            if (callback) callback();
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'CREATE_SOCIETY_ERROR' });
        }
    }
}

export function uploadFile(file: File, name: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const fileState = getState().society.files[name as keyof ListFiles];
        dispatch({ type: 'UPLOAD_FILE_REQUEST ', payload: name });
        try {
            const formData = new FormData();
            formData.append('iss', getState().account.iss.toString());
            formData.append('type', fileState.type);
            formData.append('file', file);
            const { data } = await postUserFile(formData, getState().account.iss);
            dispatch({ type: 'UPLOAD_FILE_SUCCEEDED', payload: { name, url: data.url } })
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'UPLOAD_FILE_FAILED', payload: { name, error: err } });
            dispatch(addToast({
                type: 'error',
                title: 'Le fichier n\'a pas pu être envoyé',
                description: `Une erreur est survenue lors de l'envoi du fichier: ${err}.`
            }));
        }
    }
}

export function resetFile(name: string) {
    return (dispatch: AppDispatch) => {
        dispatch({ type: 'RESET_SOCIETY_FILE', payload: name });
    };
}

export function setSocietyChoice(id: number | string) {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const societySelected = (id === 'empty') ? undefined : getState().society.societyList.data.find((society) => `${society.id}` === `${id}`);
        const birthDate = societySelected?.person?.birthday.split(' ')[0].split('-');
        const convertedData = ({
            id,
            nameSociety: societySelected?.society_name || '',
            countrySociety: societySelected?.address?.country || '',
            societyAddress: societySelected?.address?.address || '',
            societyCity: societySelected?.address?.city || '',
            societyZipCode: societySelected?.address?.zip_code || '',
            siren: societySelected?.siren || '',
            rcs: societySelected?.rcs_city || '',
            firstname: societySelected?.person?.first_name || '',
            lastname: societySelected?.person?.last_name || '',
            address: societySelected?.person?.address?.address || '',
            zipCode: societySelected?.person?.address?.zip_code || '',
            city: societySelected?.person?.address?.city || '',
            societyLegal: societySelected?.form_juridique || '',
            role: societySelected?.role_person || '',
            country: societySelected?.person?.address.country || '',
            birthday: {
                day: ((birthDate?.length === 3) && birthDate[2]) || '',
                month: ((birthDate?.length === 3) && birthDate[1]) || '',
                year: ((birthDate?.length === 3) && birthDate[0]) || '',
            },
        });
        dispatch({ type: 'SET_SOCIETY_CHOOSE', payload: convertedData });
        const convertedPerson = {
            ...societySelected?.person,
            firstname: societySelected?.person?.first_name,
            lastname: societySelected?.person?.last_name,
            address: societySelected?.person?.address?.address,
            zipCode: societySelected?.person?.address?.zip_code,
            city: societySelected?.person?.address?.city,
            country: societySelected?.person?.address?.country,
            birthday: {
                day: ((birthDate?.length === 3) && birthDate[2]) || '',
                month: ((birthDate?.length === 3) && birthDate[1]) || '',
                year: ((birthDate?.length === 3) && birthDate[0]) || '',
            },
            birthPlace: {
                ...societySelected?.person?.address_birth,
                zipCode: societySelected?.person?.address_birth?.zip_code,
            },
            phone: societySelected?.person?.phone,
        };
        if (id !== 'empty') {
            dispatch({ type: 'FETCH_INVESTOR_SUCCESS', payload: convertedPerson });
        } else {
            dispatch({ type: 'RESET_INVESTOR_FETCH' });
        }
    };
}